import React, { useState, useEffect } from "react";
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { Pencil } from "lucide-react";
import { toast } from "@/lib/toast";
import { useSponsorsUpdate } from "@/lib/api/requests/sponsors.requests";
import { ContactPersonResponseDto } from "@/lib/api/interfaces/sponsors.interface";
import { DetailsBaseDialog } from "@/util-components/dialog/base-dialogs/details-base-dialog";

interface EditContactDialogProps {
  sponsorId: string;
  existingContacts: ContactPersonResponseDto[];
  contactIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const EditContactDialog: React.FC<EditContactDialogProps> = ({
  sponsorId,
  existingContacts,
  contactIndex,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const contact = existingContacts[contactIndex];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateSponsorMutation = useSponsorsUpdate();

  useEffect(() => {
    if (open && contact) {
      setName(contact.name || "");
      setEmail(contact.email || "");
      setPhone(contact.phone || "");
      setRole(contact.role || "");
    }
  }, [open, contact]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!name.trim()) {
      toast.error("Name erforderlich", {
        description: "Bitte gib einen Namen für den Kontakt ein.",
      });
      return;
    }

    setIsSubmitting(true);

    const updatedContacts = existingContacts.map((c, idx) => {
      if (idx === contactIndex) {
        return {
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          role: role.trim() || undefined,
          mobile: c.mobile,
          avatar: (c as any).avatar ?? null,
        };
      }
      return {
        name: c.name,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        role: c.role,
        avatar: (c as any).avatar ?? null,
      };
    });

    try {
      await updateSponsorMutation.mutateAsync({
        id: sponsorId,
        data: { contacts: updatedContacts } as any,
      });

      toast.success("Kontakt aktualisiert", {
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });

      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      toast.error("Fehler beim Speichern", {
        description: "Der Kontakt konnte nicht aktualisiert werden.",
        error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onOpenChange}
      mode="edit"
      icon={Pencil}
      title="Kontakt bearbeiten"
      description="Passe die Daten des Ansprechpartners an."
      hideDelete={true}
      hideEdit={true}
      isSaving={isSubmitting}
      canSave={name.trim().length > 0}
      formId="edit-contact-form"
      detailsContent={<></>}
      editContent={(onSaveSuccess) => (
        <form id="edit-contact-form" onSubmit={(e) => {
          handleSubmit(e).then(() => {
            // onSuccess handled in handleSubmit (handleClose())
          });
        }} className="flex flex-col h-full px-5 sm:px-6 py-4 overflow-y-auto space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-contact-name">Name *</Label>
              <Input
                id="edit-contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Vor- und Nachname"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-role">Rolle / Funktion</Label>
              <Input
                id="edit-contact-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="z.B. Marketingleiter"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-email">E-Mail</Label>
              <Input
                id="edit-contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@beispiel.de"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-phone">Telefon</Label>
              <Input
                id="edit-contact-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 123 456789"
                disabled={isSubmitting}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>
          </div>
        </form>
      )}
    />
  );
};
